import { useEffect, useState } from "react";
import { MessagesSquare, Trash2, X } from "lucide-react";
import { ProUpsellLink } from "@/components/ProUpsell";

export type DrawerConversation = {
  id: string;
  title: string | null;
  last_message_at: string;
  preview: string | null;
  first_user_message: string | null;
};

interface ConversationsDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelectConversation: (id: string) => void;
  /** Permanently deletes one thread. Free for everyone — see deleteConversation.ts. */
  onDeleteConversation: (id: string) => void | Promise<void>;
  conversations: DrawerConversation[];
  loading: boolean;
  showUpgradeFooter?: boolean;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = now - then;
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "Just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  // Compare calendar days
  const d1 = new Date(then);
  const d2 = new Date(now);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.floor((startOf(d2) - startOf(d1)) / 86400000);
  if (dayDiff === 1) return "Yesterday";
  if (dayDiff < 7) return `${dayDiff}d ago`;
  return d1.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const ConversationsDrawer = ({
  open,
  onClose,
  onSelectConversation,
  onDeleteConversation,
  conversations,
  loading,
  showUpgradeFooter = false,
}: ConversationsDrawerProps) => {
  // Which row is asking "Delete this chat?". Inline rather than an
  // AlertDialog on purpose: the drawer sits at z-index 61 and a portalled
  // dialog has to out-stack it, and window.confirm() renders the raw origin
  // URL, which we cannot write brand copy into.
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  // Reopening the drawer must not land on a primed "Delete?" row from last
  // time — that is one stray tap from an irreversible delete she never asked
  // for.
  useEffect(() => {
    if (!open) setConfirmingId(null);
  }, [open]);
  // ESC to close (desktop)
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        aria-hidden={!open}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.4)",
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
          transition: "opacity 250ms ease-out",
          zIndex: 60,
        }}
      />
      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-label="Past chats"
        aria-hidden={!open}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width: "78%",
          maxWidth: 360,
          backgroundColor: "#F0F0FF",
          boxShadow: "2px 0 12px rgba(28, 26, 62, 0.15)",
          transform: open ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 250ms ease-out",
          zIndex: 61,
          display: "flex",
          flexDirection: "column",
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: 16,
            borderBottom: "0.5px solid rgba(28, 26, 62, 0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: 16,
              fontWeight: 500,
              color: "#1C1A3E",
              margin: 0,
            }}
          >
            Past Chats
          </h2>
          <button
            onClick={onClose}
            aria-label="Close past chats"
            style={{
              background: "transparent",
              border: "none",
              padding: 4,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={20} color="#54525C" />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto" }}>
          {loading ? (
            <div style={{ padding: 0 }}>
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  style={{
                    padding: "12px 16px",
                    borderBottom: "0.5px solid rgba(28, 26, 62, 0.05)",
                  }}
                >
                  <div
                    className="animate-pulse"
                    style={{
                      height: 14,
                      width: "60%",
                      background: "rgba(28,26,62,0.08)",
                      borderRadius: 4,
                      marginBottom: 8,
                    }}
                  />
                  <div
                    className="animate-pulse"
                    style={{
                      height: 12,
                      width: "85%",
                      background: "rgba(28,26,62,0.06)",
                      borderRadius: 4,
                    }}
                  />
                </div>
              ))}
            </div>
          ) : conversations.length === 0 ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 24,
                textAlign: "center",
              }}
            >
              <MessagesSquare size={48} color="#C8C8F4" />
              <p
                style={{
                  fontFamily: "'DM Serif Display', serif",
                  fontSize: 16,
                  color: "#54525C",
                  marginTop: 16,
                  marginBottom: 6,
                }}
              >
                No past chats yet
              </p>
              <p
                style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: 13,
                  color: "#54525C",
                  opacity: 0.8,
                  margin: 0,
                  maxWidth: 240,
                  lineHeight: 1.4,
                }}
              >
                Your conversations will appear here as you chat with Kin.
              </p>
            </div>
          ) : (
            conversations.map((c) => {
              const hasTitle = !!(c.title && c.title.trim());
              const fallback = (c.first_user_message || "").slice(0, 50);
              const titleText = hasTitle ? c.title! : fallback || "Untitled chat";
              const preview = c.preview ? c.preview.slice(0, 60) : "";
              const confirming = confirmingId === c.id;
              return (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    borderBottom: "0.5px solid rgba(28, 26, 62, 0.05)",
                    background: confirming ? "rgba(200, 75, 75, 0.06)" : "transparent",
                  }}
                >
                  {/* The row itself is no longer a <button>: the trash control
                      is a button, and a button cannot nest inside one. */}
                  <button
                    onClick={() => onSelectConversation(c.id)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      textAlign: "left",
                      background: "transparent",
                      border: "none",
                      padding: "12px 4px 12px 16px",
                      cursor: "pointer",
                      display: "block",
                    }}
                  >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "baseline",
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 14,
                        fontWeight: 500,
                        color: "#1C1A3E",
                        fontStyle: hasTitle ? "normal" : "italic",
                        opacity: hasTitle ? 1 : 0.75,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      {titleText}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Plus Jakarta Sans', sans-serif",
                        fontSize: 11,
                        color: "#54525C",
                        flexShrink: 0,
                      }}
                    >
                      {formatRelative(c.last_message_at)}
                    </span>
                  </div>
                  {preview && (
                      <div
                        style={{
                          marginTop: 4,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 12,
                          color: "#54525C",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {preview}
                      </div>
                    )}
                  </button>

                  {/* Delete. Two taps, never one — this cascades the thread's
                      messages away and there is no undo. */}
                  {confirming ? (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        paddingRight: 16,
                        flexShrink: 0,
                      }}
                    >
                      <button
                        onClick={() => {
                          setConfirmingId(null);
                          void onDeleteConversation(c.id);
                        }}
                        aria-label={`Permanently delete ${titleText}`}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: "8px 2px",
                          cursor: "pointer",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "#C84B4B",
                        }}
                      >
                        Delete
                      </button>
                      <button
                        onClick={() => setConfirmingId(null)}
                        aria-label="Keep this chat"
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: "8px 2px",
                          cursor: "pointer",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          fontSize: 12,
                          color: "#54525C",
                        }}
                      >
                        Keep
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmingId(c.id)}
                      aria-label={`Delete ${titleText}`}
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "12px 16px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Trash2 size={16} color="#54525C" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
        {showUpgradeFooter && (
          <div
            style={{
              padding: "12px 16px",
              borderTop: "0.5px solid rgba(28, 26, 62, 0.08)",
              backgroundColor: "#F0F0FF",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 12,
              color: "#54525C",
              lineHeight: 1.5,
              paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))",
            }}
          >
            Free tier: showing your 5 most recent chats.{" "}
            {/* Issue #50: was dead-end text ("Kin Pro launching soon. 💜"). */}
            <ProUpsellLink
              label="See what's in Kin Pro 💜"
              fontSize={12}
              onNavigate={onClose}
            />
          </div>
        )}
      </aside>
    </>
  );
};

export default ConversationsDrawer;
